import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import FSSLogo from "./FSSLogo";

const SERVICE_LINKS = [
  { to: "/services", label: "All Fire Safety Services" },
  { to: "/services/fire-alarms", label: "Fire Alarm Systems" },
  { to: "/services/emergency-lighting", label: "Emergency Lighting" },
  { to: "/services/fire-extinguishers", label: "Fire Extinguishers" },
  { to: "/services/testing-certification", label: "Testing & Certification" },
  { to: "/services/maintenance", label: "Planned Maintenance" },
  { to: "/services/repairs-upgrades", label: "Repairs & Upgrades" },
];

const CCTV_LINKS = [
  { to: "/cctv-security", label: "CCTV & Security Overview" },
  { to: "/cctv-security#cctv-installation", label: "CCTV Installation" },
  { to: "/cctv-security#ip-hd", label: "IP & HD Camera Systems" },
  { to: "/cctv-security#analytics", label: "Video Analytics & Remote Viewing" },
  { to: "/cctv-security#intruder", label: "Intruder Alarms" },
  { to: "/cctv-security#perimeter", label: "Perimeter Security" },
];

const ACCESS_LINKS = [
  { to: "/access-control", label: "Access Control Overview" },
  { to: "/access-control#systems", label: "Access Control Systems" },
  { to: "/access-control#credentials", label: "Card, Fob & Mobile Access" },
  { to: "/access-control#biometric", label: "Biometric Access" },
  { to: "/access-control#intercoms", label: "Intercom Systems" },
  { to: "/access-control#visitor", label: "Visitor & Entry Management" },
];

const MONITORING_LINKS = [
  { to: "/monitoring", label: "Monitoring Overview" },
  { to: "/monitoring#alarm", label: "Alarm Monitoring" },
  { to: "/monitoring#cctv", label: "Live CCTV Monitoring" },
  { to: "/monitoring#verification", label: "Remote CCTV Verification" },
  { to: "/monitoring#panic", label: "Personal & Panic Alarms" },
  { to: "/monitoring#lone-worker", label: "Lone Worker Protection" },
];

const NAV = [
  { to: "/", label: "Home" },
  { label: "Fire Safety", children: SERVICE_LINKS },
  { label: "CCTV & Security", children: CCTV_LINKS },
  { label: "Access Control", children: ACCESS_LINKS },
  { label: "Monitoring", children: MONITORING_LINKS },
  { to: "/industries", label: "Industries" },
  { to: "/maintenance-support", label: "Maintenance" },
  { to: "/about", label: "About FSS" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  return (
    <>
      {/* Top utility strip */}
      <div className="hidden md:block bg-slate-900 text-slate-200 text-xs">
        <div className="fss-container flex items-center justify-between h-9">
          <div className="flex items-center gap-6">
            <span className="text-slate-400">
              Facilities &amp; Safety Systems <span className="text-slate-500">·</span>{" "}
              <span className="text-white font-semibold">Ireland</span>
            </span>
            <span className="text-slate-400">Nationwide Coverage</span>
          </div>
          <div className="flex items-center gap-5">
            <a
              href="tel:018351454"
              className="flex items-center gap-2 hover:text-white transition-colors"
              data-testid="header-top-phone"
            >
              <Phone className="w-3.5 h-3.5 text-red-500" />
              <span>01 835 1454</span>
            </a>
            <a
              href="mailto:service@fssl.ie"
              className="hover:text-white transition-colors"
              data-testid="header-top-email"
            >
              service@fssl.ie
            </a>
          </div>
        </div>
      </div>

      <header
        className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200"
        data-testid="site-header"
      >
        <div className="fss-container flex items-center justify-between h-20">
          <FSSLogo />

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label} className="relative group">
                  <button
                    className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-slate-700 hover:text-green-700 transition-colors"
                    data-testid="nav-services"
                    aria-haspopup="true"
                  >
                    {item.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="absolute left-0 top-full pt-2 hidden group-hover:block">
                    <div className="bg-white border border-slate-200 shadow-lg rounded-sm w-72 py-2">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          className={({ isActive }) =>
                            `block px-5 py-3 text-sm font-medium border-l-2 transition-colors ${
                              isActive
                                ? "border-green-700 text-green-700 bg-slate-50"
                                : "border-transparent text-slate-700 hover:border-green-700 hover:text-green-700 hover:bg-slate-50"
                            }`
                          }
                          data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-semibold transition-colors ${
                      isActive ? "text-green-700" : "text-slate-700 hover:text-green-700"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="hidden md:inline-flex fss-btn fss-btn-primary"
              data-testid="header-quote-cta"
            >
              Free Site Assessment
            </Link>
            <button
              className="lg:hidden p-2 text-slate-700"
              onClick={() => setOpen(!open)}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div className="fss-container py-4 flex flex-col">
              {NAV.map((item) =>
                item.children ? (
                  <div key={item.label} className="border-b border-slate-100">
                    <button
                      className="w-full flex items-center justify-between py-3 text-sm font-semibold text-slate-700"
                      onClick={() => setOpenSection(openSection === item.label ? null : item.label)}
                      aria-expanded={openSection === item.label}
                      data-testid={`mobile-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}-toggle`}
                    >
                      {item.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${openSection === item.label ? "rotate-180" : ""}`}
                      />
                    </button>
                    {openSection === item.label && (
                      <div className="pl-3 pb-2">
                        {item.children.map((s) => (
                          <Link
                            key={s.to}
                            to={s.to}
                            onClick={() => setOpen(false)}
                            className="block py-2 text-sm font-medium text-slate-600 hover:text-green-700"
                          >
                            {s.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <MobileLink key={item.to} to={item.to} label={item.label} onClick={() => setOpen(false)} />
                )
              )}

              <MobileLink to="/faqs" label="FAQs" onClick={() => setOpen(false)} />

              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="fss-btn fss-btn-primary mt-4"
              >
                Get a Free Quote
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}

function MobileLink({ to, label, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="py-3 text-sm font-semibold text-slate-700 hover:text-green-700 border-b border-slate-100"
    >
      {label}
    </Link>
  );
}
